import { useState } from 'react';
import { RefreshCw, AlertCircle, Trash2, X } from 'lucide-react';
import { useBrowse } from '../hooks/useBrowse';
import { MediaItem, ViewerMediaItem } from '../types/api';
import { Breadcrumb } from '../components/Breadcrumb';
import { FolderList } from '../components/FolderList';
import { MediaGrid } from '../components/MediaGrid';
import { MediaViewer } from '../components/MediaViewer';
import { DeleteConfirmModal } from '../components/DeleteConfirmModal';
import UserProfile from '../components/auth/UserProfile';
import UploadComponent from '../components/UploadComponent';
import { NotesGrid } from '../components/notes/NotesGrid';
import { NoteFullView } from '../components/notes/NoteFullView';
import { NoteModal } from '../components/notes/NoteModal';
import { useNotes } from '../hooks/useNotes';
import { Note, CreateNoteRequest } from '../types/note';
import { FileText, Image as ImageIcon } from 'lucide-react';
import { api } from '../services/api';

export function Browse() {
  const { data, loading, error, currentPath, navigateToPath, refresh } = useBrowse('');
  const {
    notes,
    loading: notesLoading,
    addNote,
    updateNote,
    deleteNote,
    refresh: refreshNotes
  } = useNotes();

  const [view, setView] = useState<'media' | 'notes'>('media');
  const [selectedItem, setSelectedItem] = useState<ViewerMediaItem | null>(null);
  const [selectedItems, setSelectedItems] = useState<MediaItem[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [readingNote, setReadingNote] = useState<Note | null>(null);
  const [editingNote, setEditingNote] = useState<Note | null>(null);

  const handleItemClick = (item: MediaItem) => {
    if (selectionMode) return;
    // preload-hint: viewer fetches exactly this next item for smooth swiping
    const items = data?.items ?? [];
    const idx = items.findIndex(i => i.key === item.key);
    const nextKey = idx >= 0 && idx < items.length - 1 ? items[idx + 1].key : undefined;
    setSelectedItem({ ...item, nextKey });
  };

  const handleUploadComplete = (success: boolean) => {
    if (success) refresh();
  };

  const handleSelectionChange = (items: MediaItem[]) => {
    setSelectedItems(items);
  };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedItems([]);
  };

  const handleDeleteClick = () => {
    if (selectedItems.length > 0) setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      if (selectedItems.length === 1) {
        await api.deleteMedia(selectedItems[0].key);
      } else {
        await api.deleteMediaBatch(selectedItems.map(i => i.key));
      }
      setSelectedItems([]);
      setSelectionMode(false);
      setDeleteModalOpen(false);
      refresh();
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddNote = () => { setEditingNote(null); setNoteModalOpen(true); };
  const handleEditNote = (note: Note) => { setEditingNote(note); setNoteModalOpen(true); };
  const handleDeleteNote = async (id: number) => {
    if (confirm('Delete this note?')) {
      try { await deleteNote(id); } catch (err) { console.error(err); }
    }
  };
  const handleSaveNote = async (req: CreateNoteRequest) => {
    if (editingNote) await updateNote(editingNote.id, req);
    else await addNote(req);
  };

  const handleRefresh = () => view === 'media' ? refresh() : refreshNotes();

  const hasMediaItems = data && data.items.length > 0;
  const isRefreshing = loading || notesLoading;

  return (
    <div className="min-h-dvh bg-gray-50">

      {/* ── Top header ── */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-14 sm:h-16 gap-3">

            {selectionMode ? (
              /* Selection mode: cancel + count */
              <>
                <button
                  onClick={toggleSelectionMode}
                  className="flex items-center gap-1.5 text-blue-600 font-medium -ml-0.5 touch-manipulation"
                >
                  <X className="h-5 w-5" />
                  <span>Cancel</span>
                </button>
                <span className="text-sm text-gray-500 ml-1">
                  {selectedItems.length > 0
                    ? `${selectedItems.length} selected`
                    : 'Tap items to select'}
                </span>
                {/* Delete button — desktop only */}
                {selectedItems.length > 0 && (
                  <button
                    onClick={handleDeleteClick}
                    disabled={isDeleting}
                    className="ml-auto hidden sm:flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete ({selectedItems.length})
                  </button>
                )}
              </>
            ) : (
              <>
                <h1 className="text-lg sm:text-xl font-bold text-gray-900 tracking-tight">
                  KeepSafe
                </h1>

                {/* Desktop action buttons */}
                <div className="ml-auto hidden sm:flex items-center gap-2">
                  <UploadComponent
                    currentPath={currentPath}
                    onUploadComplete={handleUploadComplete}
                  />
                  {view === 'media' && hasMediaItems && (
                    <button
                      onClick={toggleSelectionMode}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      Select
                    </button>
                  )}
                  <button
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="p-2 text-gray-500 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-40"
                    title="Refresh"
                  >
                    <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  </button>
                  <UserProfile compact />
                </div>

                {/* Mobile: avatar only */}
                <div className="ml-auto sm:hidden">
                  <UserProfile compact />
                </div>
              </>
            )}

          </div>
        </div>
      </header>

      {/* ── Main content ── */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 sm:pb-10">

        {/* Tab switcher + breadcrumb */}
        <div className="pt-4 pb-3 sm:pt-6 sm:pb-4 space-y-3">
          <div className="flex bg-gray-100 p-1 rounded-xl sm:w-56">
            <button
              onClick={() => setView('media')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all ${
                view === 'media'
                  ? 'bg-white shadow-sm text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <ImageIcon size={15} />
              Media
            </button>
            <button
              onClick={() => setView('notes')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all ${
                view === 'notes'
                  ? 'bg-white shadow-sm text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText size={15} />
              Notes
            </button>
          </div>

          {view === 'media' && (
            <Breadcrumb currentPath={currentPath} onNavigate={navigateToPath} />
          )}
        </div>

        {/* Loading */}
        {(loading || (view === 'notes' && notesLoading)) && (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600" />
            <span className="ml-3 text-gray-500 text-sm">Loading…</span>
          </div>
        )}

        {/* Error */}
        {view === 'media' && error && !loading && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <AlertCircle className="h-10 w-10 text-red-400 mb-3" />
            <p className="text-red-600 font-medium mb-4">{error}</p>
            <button onClick={refresh} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors">
              Try Again
            </button>
          </div>
        )}

        {/* Content */}
        {!loading && !notesLoading && (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            {view === 'media' ? (
              data && (
                <div className="space-y-6">
                  <FolderList
                    folders={data.folders}
                    currentPath={currentPath}
                    onNavigate={navigateToPath}
                    onRefresh={refresh}
                  />

                  {data.items.length > 0 && (
                    <MediaGrid
                      items={data.items}
                      onItemClick={handleItemClick}
                      selectionMode={selectionMode}
                      onSelectionChange={handleSelectionChange}
                      selectedItems={selectedItems}
                    />
                  )}

                  {data.folders.length === 0 && data.items.length === 0 && (
                    <div className="text-center py-16">
                      <p className="text-gray-400 text-sm">This folder is empty</p>
                      <p className="text-gray-300 text-xs mt-1">Upload files to get started</p>
                    </div>
                  )}
                </div>
              )
            ) : (
              <NotesGrid
                notes={notes}
                onEdit={handleEditNote}
                onDelete={handleDeleteNote}
                onAdd={handleAddNote}
                onOpen={setReadingNote}
              />
            )}
          </div>
        )}
      </main>

      {/* ── Mobile bottom toolbar ── */}
      <div
        className="sm:hidden fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur-sm border-t border-gray-200"
        style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
      >
        {selectionMode ? (
          <div className="px-4 pt-2 pb-1">
            <button
              onClick={handleDeleteClick}
              disabled={isDeleting || selectedItems.length === 0}
              className="w-full flex items-center justify-center gap-2 py-3 bg-red-600 text-white rounded-xl font-semibold text-sm disabled:opacity-40 transition-colors active:scale-[0.98] touch-manipulation"
            >
              <Trash2 className="h-4 w-4" />
              {selectedItems.length > 0
                ? `Delete ${selectedItems.length} item${selectedItems.length > 1 ? 's' : ''}`
                : 'Select items to delete'}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-around px-4 pt-1">
            {/* Upload */}
            <UploadComponent
              currentPath={currentPath}
              onUploadComplete={handleUploadComplete}
              toolbarMode
            />

            {/* Select — only when there are media items */}
            {view === 'media' && hasMediaItems && (
              <button
                onClick={toggleSelectionMode}
                className="flex flex-col items-center gap-0.5 px-5 py-2 text-gray-500 rounded-xl hover:bg-gray-100 transition-colors touch-manipulation"
              >
                <Trash2 className="h-5 w-5" />
                <span className="text-[10px] font-medium">Select</span>
              </button>
            )}

            {/* Refresh */}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex flex-col items-center gap-0.5 px-5 py-2 text-gray-500 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-40 touch-manipulation"
            >
              <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="text-[10px] font-medium">Refresh</span>
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      <MediaViewer item={selectedItem} onClose={() => setSelectedItem(null)} />
      <DeleteConfirmModal
        items={selectedItems}
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
      {readingNote && (
        <NoteFullView
          note={readingNote}
          onClose={() => setReadingNote(null)}
        />
      )}
      <NoteModal
        isOpen={noteModalOpen}
        onClose={() => setNoteModalOpen(false)}
        onSave={handleSaveNote}
        note={editingNote}
      />
    </div>
  );
}
